import { ContactUs } from "../components/ContactUs";
import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Nav } from "../components/Nav";
import { Check, Upload, Loader2, MapPin, Lock, Heart } from "lucide-react";

const TIER_ORDER = ["city", "state", "nationwide"];

export default function Advertise() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [tiers, setTiers] = useState({});
  const [tier, setTier] = useState("state");
  const [form, setForm] = useState({ business_name: "", headline: "", cta_text: "Learn More", link_url: "", target_city: "", target_state: "" });
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/ads/tiers").then(({ data }) => setTiers(data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const promptSignup = (msg) => {
    toast(msg, { description: "Already have an account? Log in. New here? It's free to sign up — then come right back to launch your ad." });
    navigate("/login", { state: { redirect: "/advertise" } });
  };

  // Open the file picker only when signed in; otherwise nudge to sign up.
  const openPicker = () => {
    if (!user) { promptSignup("Log in or sign up to add your ad image"); return; }
    fileRef.current?.click();
  };

  // Resize/compress to fit the ad space (max 1200px) and normalise to JPEG.
  const resizeImage = (file, max = 1200) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > max || height > max) {
        const scale = Math.min(max / width, max / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("resize failed"))), "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { promptSignup("Log in or sign up to add your ad image"); e.target.value = ""; return; }
    setUploading(true);
    let blob;
    try {
      blob = await resizeImage(file, 1200);
    } catch {
      toast.error("Please upload a JPG, PNG, or WEBP image (HEIC photos aren't supported — pick 'Most Compatible' on iPhone, or take a screenshot).");
      setUploading(false); e.target.value = ""; return;
    }
    try {
      const fd = new FormData();
      fd.append("file", blob, "ad-image.jpg");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.url);
      toast.success("Image uploaded!");
    } catch (err) {
      if (err.response?.status === 401) promptSignup("Please log in or sign up to advertise");
      else toast.error("Couldn't upload that image. Please try again.");
    } finally {
      setUploading(false); e.target.value = "";
    }
  };

  const checkout = async () => {
    if (!user) { promptSignup("Log in or sign up to launch your campaign"); return; }
    if (!form.business_name || !form.headline || !form.link_url) { alert("Please fill in business name, headline and link."); return; }
    if (tier === "city" && !form.target_city) { alert("Enter a target city."); return; }
    if (tier === "state" && !form.target_state) { alert("Enter a target state."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ads/campaigns", {
        ...form, tier, image_url: imageUrl, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      alert("Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-7xl mx-auto px-5 py-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 nb-card px-4 py-1.5 bg-[#A8E6CF] mb-4">
            <MapPin className="w-4 h-4" /> <span className="text-xs font-bold uppercase tracking-widest">Geo-targeted advertising</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold">Reach happy people, right when they smile</h1>
          <p className="text-[#4A4A4A] mt-3">Your ad shows after someone sends a greeting. Pick your reach below.</p>
          <p className="text-xs text-[#4A4A4A] mt-2 italic">As a new platform, ad reach and impressions vary with local activity and are not guaranteed.</p>
        </div>

        {/* Pricing tiers */}
        <div className="grid md:grid-cols-3 gap-6 mt-10" data-testid="pricing-tiers">
          {TIER_ORDER.map((t) => {
            const info = tiers[t];
            const selected = tier === t;
            const featured = t === "nationwide";
            return (
              <button key={t} onClick={() => setTier(t)} data-testid={`tier-${t}`}
                className={`nb-card p-7 text-left transition-all relative ${selected ? "shadow-[8px_8px_0px_#0A0A0A] -translate-y-1" : ""} ${featured ? "bg-[#FFB5A7]" : "bg-white"}`}>
                {featured && <span className="absolute -top-3 left-6 nb-card bg-[#F9F871] px-3 py-0.5 text-xs font-bold uppercase tracking-widest">Best reach</span>}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest">{info?.label || t}</span>
                  {selected && <span className="w-6 h-6 grid place-items-center rounded-full bg-[#0A0A0A] text-white"><Check className="w-4 h-4" /></span>}
                </div>
                <div className="mt-3"><span className="text-4xl font-display font-bold">${info ? info.price : "—"}</span><span className="text-[#4A4A4A] font-semibold">/mo</span></div>
                <p className="text-sm text-[#4A4A4A] mt-2">{info?.desc}</p>
              </button>
            );
          })}
        </div>

        {/* Campaign form */}
        <div className="grid lg:grid-cols-2 gap-8 mt-12 items-start">
          <div className="nb-card p-7" data-testid="campaign-form">
            <h2 className="text-xl font-display font-bold mb-5">Your ad details</h2>
            <div className="space-y-4">
              <Input label="Business name" value={form.business_name} onChange={set("business_name")} testid="ad-business" placeholder="Joe's Coffee" />
              <Input label="Headline" value={form.headline} onChange={set("headline")} testid="ad-headline" placeholder="Free coffee this week!" />
              <Input label="Button text" value={form.cta_text} onChange={set("cta_text")} testid="ad-cta" placeholder="Learn More" />
              <Input label="Link URL" value={form.link_url} onChange={set("link_url")} testid="ad-link" placeholder="https://yourbusiness.com" />

              {tier === "city" && <Input label="Target city" value={form.target_city} onChange={set("target_city")} testid="ad-city" placeholder="Austin" />}
              {tier === "state" && <Input label="Target state" value={form.target_state} onChange={set("target_state")} testid="ad-state" placeholder="Texas" />}

              <div>
                <span className="text-xs font-bold uppercase tracking-widest">Ad image</span>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="ad-image-input" />
                <button onClick={openPicker} disabled={uploading} data-testid="ad-image-btn" className="nb-btn bg-[#A8E6CF] px-4 py-3 mt-1.5 w-full">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />} {imageUrl ? "Change image" : "Upload image"}
                </button>
                {!user && <p className="text-xs text-[#4A4A4A] mt-1.5">Log in or sign up free to add your ad image.</p>}
              </div>

              <button onClick={checkout} disabled={submitting} data-testid="checkout-btn" className="nb-btn bg-[#FFB5A7] w-full py-4 text-lg mt-2">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                {tiers[tier] ? `Pay $${tiers[tier].price}/mo & launch` : "Launch campaign"}
              </button>
              <div className="nb-border rounded-xl bg-[#FDFBF7] p-4 mt-3 text-sm text-[#4A4A4A] leading-relaxed" data-testid="payment-note">
                <p className="font-bold text-[#0A0A0A] flex items-center gap-2"><Lock className="w-4 h-4" /> Secure recurring billing</p>
                <p className="mt-1">
                  Your campaign is a <b>monthly recurring subscription</b> ($
                  {tiers[tier] ? tiers[tier].price : "—"}/month). Payments are processed securely by
                  <b> Stripe</b>, our third-party payment provider — we never store your card details.
                </p>
                <p className="mt-2">
                  To pause or <b>cancel your ads, log in to manage your subscription through Stripe's billing portal</b>.
                  Cancelling stops future charges; your current month stays active until it ends.
                </p>
              </div>
              {!user && <p className="text-center text-sm text-[#4A4A4A]">You'll be asked to log in or sign up first.</p>}
            </div>
          </div>

          {/* Ad preview */}
          <div className="lg:sticky lg:top-24">
            <span className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Live preview</span>
            <div className="nb-card nb-shadow-lg p-0 overflow-hidden mt-2 max-w-md">
              <div className="px-4 py-2 bg-[#F9F871] border-b-2 border-[#0A0A0A] text-xs font-bold uppercase tracking-widest">Sponsored</div>
              {imageUrl ? <img src={resolveImg(imageUrl)} alt="ad" className="w-full h-48 object-cover border-b-2 border-[#0A0A0A]" />
                : <div className="w-full h-48 grid place-items-center bg-gray-100 border-b-2 border-[#0A0A0A] text-[#4A4A4A] font-semibold">Your image here</div>}
              <div className="p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">{form.business_name || "Your business"}</div>
                <h3 className="text-2xl font-display font-bold mt-1">{form.headline || "Your headline"}</h3>
                <span className="nb-btn bg-[#FFB5A7] px-5 py-2 mt-4">{form.cta_text || "Learn More"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer className="border-t-2 border-[#0A0A0A] py-8 text-center text-sm text-[#4A4A4A]">
        <div className="flex items-center justify-center flex-wrap gap-4 mb-3">
          <Link to="/terms" data-testid="footer-terms" className="font-semibold underline">Terms of Service</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="font-semibold underline">Privacy Policy</Link>
          <Link to="/advertise" className="font-semibold underline">Advertise</Link>
          <ContactUs />
        </div>
        SendOccasions — made with <Heart className="w-4 h-4 inline" /> · Sending greetings is always free.
      </footer>
    </div>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest">{label}</span>
      <input {...props} data-testid={testid}
        className="w-full nb-border rounded-xl px-4 py-3 bg-[#FDFBF7] mt-1.5 focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" />
    </label>
  );
}
